<template>
	<div class="row">
		<div class="column twelve">
			<div class="relative mx-auto rounded shadow search-input-container block z-5">
				<div class="flex">
					<input
						v-model="search"
						class="search-input"
						type="text"
						placeholder="Search by address, zip, city, or county"
					/>
					<button class="search-input-button icon-m-search" aria-label="Search"></button>
				</div>

				<div class="border-t border-gray100 border-solid flex">
					<div
						class="hidden sm:flex relative flex-grow px-25 py-30 cursor-pointer listing-filter-category"
						@click.stop
						@click="toggleFilter('listingType')"
						:class="listingTypeClass"
					>
						<div class="text-gray600 mr-15">{{ listingTypeText }}</div>
						<div class="listing-filter-category-dropdown-arrow"></div>

						<div @click.stop class="listing-filter-options">
							<div class="flex flex-col items-start p-40 bg-white rounded w-300">
								<vCheck
									v-for="(value, option) in listingTypeOptions"
									v-model="listingTypeOptions[option]"
									:label="formatLabel(option)"
									:key="'more-type-'+option"
									class="py-8"
								/>
							</div>
						</div>
					</div>

					<div
						class="hidden md:flex relative flex-grow px-25 py-30 cursor-pointer listing-filter-category"
						@click.stop
						@click="toggleFilter('type')"
						:class="typeClass"
					>
						<div class="text-gray600 mr-15">Type</div>
						<div class="listing-filter-category-dropdown-arrow"></div>

						<div @click.stop class="listing-filter-options">
							<div class="flex flex-col items-start p-40 bg-white rounded w-300">
								<vCheck
									v-for="(value, option) in typeOptions"
									v-model="typeOptions[option]"
									:label="formatLabel(option)"
									:key="'more-type-'+option"
									class="py-8"
								/>
								<!-- <vSelect :options="typeOptions" v-model="propertyType" class="listing-search-input-container w-full" /> -->
							</div>
						</div>
					</div>

					<div
						class="hidden lg:flex listing-filter-category"
						@click.stop
						@click="toggleFilter('area')"
						:class="areaClass"
					>
						<div class="mr-15 text-gray600">{{ areaText + ' Sf' }}</div>

						<div class="listing-filter-category-dropdown-arrow"></div>

						<div @click.stop class="listing-filter-options">
							<div class="flex items-center p-40 bg-white rounded">
								<SearchInput v-model="area.min" placeholder="Min" unit-after="sf" />

								<div class="text-gray100 mx-7">-</div>

								<SearchInput v-model="area.max" placeholder="Max" unit-after="sf" />
							</div>
						</div>
					</div>

					<div
						class="hidden lg:flex listing-filter-category"
						@click.stop
						@click="toggleFilter('acres')"
						:class="acresClass"
					>
						<div class="mr-15 text-gray600">{{ acresText + ' Acres' }}</div>

						<div class="listing-filter-category-dropdown-arrow"></div>

						<div @click.stop class="listing-filter-options">
							<div class="flex items-center p-40 bg-white rounded">
								<SearchInput v-model="acres.min" placeholder="Min" unit-after="acres" />

								<div class="text-gray100 mx-7">-</div>

								<SearchInput v-model="acres.max" placeholder="Max" unit-after="acres" />
							</div>
						</div>
					</div>
					<div
						class="listing-filter-category"
						@click.stop
						@click="toggleFilter('more')"
						:class="moreClass"
					>
						<div class="text-gray600">
							<span class="inline md:hidden">View Filters</span>
							<span class="hidden md:inline">More</span>
						</div>
						<div class="listing-filter-category-dropdown-arrow"></div>

						<div @click.stop class="listing-filter-options more">
							<div class="flex flex-wrap">
								<!-- Listing Type -->
								<div class="w-full sm:hidden">
									<div class="text-gray600 my-10">Listing Type</div>
									<vCheck
										v-for="(value, option) in listingTypeOptions"
										v-model="listingTypeOptions[option]"
										:label="formatLabel(option)"
										:key="'more-type-'+option"
										class="py-8"
									/>
								</div>

								<!-- Closed Date -->
								<div class="w-full mt-15 sm:mt-0" v-if="salesDatabase">
									<div class="text-gray600 my-10">Date Closed</div>
									<div class="flex justify-between items-center">
										<CalendarInput
											v-model="closed.min"
											placeholder="Start"
											:inputAttributes="{readonly: true}"
											:startWeekOnSunday="true"
											:format="'MM-DD-YYYY'"
										/>
										<div class="text-gray100 mx-7">-</div>
										<CalendarInput
											v-model="closed.max"
											placeholder="End"
											:inputAttributes="{readonly: true}"
											:startWeekOnSunday="true"
											:format="'MM-DD-YYYY'"
										/>
									</div>
								</div>

								<!-- Price -->
								<div class="w-full mt-15" v-if="listingTypeActive" :class="{ 'sm:mt-0': !salesDatabase }">
									<div class="text-gray600 my-10">Price</div>
									<div class="flex justify-between items-center">
										<SearchInput v-model="price.min" placeholder="Min" unitBefore="$" />
										<div class="text-gray100 mx-7">-</div>
										<SearchInput v-model="price.max" placeholder="Max" unitBefore="$" />
										<div class="mx-7" v-if="listingTypeOptions.lease"></div>
										<vSelect
											:options="leaseRateOptions"
											v-model="leaseRate"
											v-if="listingTypeOptions.lease"
											class="flex-no-shrink"
										/>
									</div>
								</div>

								<!-- Square footage -->
								<div class="lg:hidden w-full sm:w-1/2 sm:pr-20 mt-15">
									<div class="text-gray600 my-10">Square Footage</div>
									<div class="flex justify-between items-center">
										<SearchInput v-model="area.min" placeholder="Min" unit-after="sf" />
										<div class="text-gray100 mx-7">-</div>
										<SearchInput v-model="area.max" placeholder="Max" unit-after="sf" />
									</div>
								</div>

								<!-- Acreage -->
								<div class="lg:hidden w-full sm:w-1/2 sm:pl-20 mt-15">
									<div class="text-gray600 my-10">Acreage</div>
									<div class="flex justify-between items-center">
										<SearchInput v-model="acres.min" placeholder="Min" unit-after="acres" />
										<div class="text-gray100 mx-7">-</div>
										<SearchInput v-model="acres.max" placeholder="Max" unit-after="acres" />
									</div>
								</div>

								<!-- Type -->
								<div class="w-full md:hidden mt-15">
									<div class="text-gray600 my-10">Type</div>
									<vCheck
										v-for="(value, option) in typeOptions"
										v-model="typeOptions[option]"
										:label="formatLabel(option)"
										:key="'type-'+option"
										class="py-8"
									/>
									<!-- <vSelect :options="typeOptions" v-model="propertyType" class="listing-search-input-container w-full" /> -->
								</div>

								<!-- Best Use -->
								<div class="w-full mt-15">
									<vSelect label="Best Use" :options="usages" v-model="usage" class="w-full" />
								</div>

								<!-- City -->
								<div class="w-full sm:w-1/2 sm:pr-20 mt-15">
									<vSelect label="City" :options="cities" v-model="city" class="w-full" />
								</div>

								<!-- County -->
								<div class="w-full sm:w-1/2 sm:pr-20 mt-15">
									<vSelect label="County" :options="counties" v-model="county" class="w-full" />
								</div>

								<!-- Status -->
								<div v-if="!salesDatabase" class="w-full sm:w-1/2 sm:pr-20  mt-15">
									<vSelect label="Status" :options="statusOptions" v-model="status" class="w-full" />
								</div>

								<!-- Brokers -->
								<div class="w-full mt-15" :class="{ 'sm:w-1/2': !salesDatabase }">
									<vSelect label="Brokers" :options="brokers" v-model="broker" class="w-full" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import SearchInput from "./search-input.vue";
	import CalendarInput from "./calendar-input.vue";
	import vSelect from "../general/v-select.vue";
	import vCheck from "../general/v-check.vue";
	import addCommas from "../../helpers/addCommas";
	import formatLabel from "../../helpers/formatLabel";

	// Builds public property and authenticated sales-database Algolia filters.
	export default {
		props: {
			initialSearch: { type: String, default: '' },
			initialType: { type: String, default: '' },
			initialCommunity: { type: String, default: '' },
			windowSize: { type: String, default: 'md' },
			params: { type: Object, required: true },
		},
		emits: ['updateSearchParams'],
		data() {
			return {
				search: "",
				minyear: "",
				community: this.initialCommunity,
				waterfront: "",
				selectedFilter: null,
				features: "",
				status: "",
				broker: "",
				city: "",
				county: "",
				usage: "",
				price: {
					min: "",
					max: ""
				},
				leaseRateOptions: {
					mo: "/mo",
					sf: "/sf"
				},
				leaseRate: "",
				acres: {
					min: "",
					max: ""
				},
				area: {
					min: "",
					max: ""
				},
				closed: {
					min: "",
					max: ""
				},
				lot: {
					min: "",
					max: ""
				},
				listingTypeOptions: {
					sale: true,
					lease: true
				},
				typeOptions: {
					land: true,
					office: true,
					retail: true,
					residential: true,
					industrial: true,
					specialty: true,
					healthcare: true,
					hospitality: true,
					entertainment: true
				},
				statusOptions: { "": "" },
				brokers: { "": "" },
				cities: { "": "" },
				counties: { "": "" },
				usages: { "": "" }
			};
		},
		computed: {
			listingTypeActive() {
				return !Object.values(this.listingTypeOptions).every(
					(val, i, arr) => val === arr[0]
				);
			},
			listingTypeText() {
				var sale = this.listingTypeOptions.sale;
				var lease = this.listingTypeOptions.lease;
				if (sale == lease) {
					return "Sale & Lease";
				} else if (sale) {
					return "Sale";
				} else if (lease) {
					return "Lease";
				}
			},
			areaActive() {
				return this.area.min || this.area.max ? true : false;
			},
			areaText() {
				const min = this.area.min;
				const max = this.area.max;

				if (min && max) {
					return `${min} - ${max}`;
				} else if (min) {
					return `${min}+`;
				} else if (max) {
					return `Up to ${max}`;
				} else {
					return "";
				}
			},
			acresActive() {
				return this.acres.min || this.acres.max ? true : false;
			},
			acresText() {
				const min = this.acres.min;
				const max = this.acres.max;

				if (min && max) {
					return `${min} - ${max}`;
				} else if (min) {
					return `${min}+`;
				} else if (max) {
					return `Up to ${max}`;
				} else {
					return "";
				}
			},
			typeActive() {
				return !Object.values(this.typeOptions).every(
					(val, i, arr) => val === arr[0]
				);
			},
			moreActive() {
				return this.minyear ||
					this.community ||
					this.waterfront ||
					this.features ||
					this.broker ||
					this.status || 
					this.city ||
					this.county ||
					this.usage ||
					this.closed.min ||
					this.closed.max ||
					this.price.min ||
					this.price.max ||
					(this.typeActive && ["sm", "md"].includes(this.windowSize)) ||
					((this.area.min || this.area.max) && this.windowSize == "sm") ||
					((this.acres.min || this.acres.max) && this.windowSize == "sm")
					? true
					: false;
			},
			priceClass() {
				var priceClass = "";
				if (this.priceActive) {
					priceClass += "listing-filter-category-active ";
				}
				if (this.selectedFilter == "price") {
					priceClass += "active ";
				}
				return priceClass;
			},
			areaClass() {
				var areaClass = "";
				if (this.areaActive) {
					areaClass += "listing-filter-category-active ";
				}
				if (this.selectedFilter == "area") {
					areaClass += "active ";
				}
				return areaClass;
			},
			acresClass() {
				var acresClass = "";
				if (this.acresActive) {
					acresClass += "listing-filter-category-active ";
				}
				if (this.selectedFilter == "acres") {
					acresClass += "active ";
				}
				return acresClass;
			},
			listingTypeClass() {
				var listingTypeClass = "";
				if (this.listingTypeActive) {
					listingTypeClass += "listing-filter-category-active ";
				}
				if (this.selectedFilter == "listingType") {
					listingTypeClass += "active ";
				}
				return listingTypeClass;
			},
			typeClass() {
				var typeClass = "";
				if (this.typeActive) {
					typeClass += "listing-filter-category-active ";
				}
				if (this.selectedFilter == "type") {
					typeClass += "active ";
				}
				return typeClass;
			},
			moreClass() {
				var moreClass = "";
				if (this.moreActive) {
					moreClass += "listing-filter-category-active ";
				}
				if (this.selectedFilter == "more") {
					moreClass += "active ";
				}
				return moreClass;
			},
			salesDatabase() {
				return window.location.pathname.substr(1) == "sales";
			}
		},
		watch: {
			listingTypeOptions: {
				handler() {
					if (
						this.listingTypeOptions.sale ==
						this.listingTypeOptions.lease
					) {
						this.leaseRate = "";
						this.price.max = "";
						this.price.min = "";
					} else if (
						this.listingTypeOptions.lease &&
						this.leaseRate == ""
					) {
						this.leaseRate = "mo";
					}
				},
				deep: true
			},
			compoundProperty() {
				this.emitParams();
			}
		},
		mounted() {
			window.addEventListener("click", this.closeModals);

			// If there's something in the hash, parse it out
			if (window.location.search) {
				var hash = window.location.search;
				this.parseHash(hash);
			}

			this.emitParams();

			this.$watch(
				() => ({
					search: this.search,
					status: this.status,
					broker: this.broker,
					city: this.city,
					county: this.county,
					usage: this.usage,
					price: this.price,
					closed: this.closed,
					leaseRateOptions: this.leaseRateOptions,
					leaseRate: this.leaseRate,
					acres: this.acres,
					area: this.area,
					listingTypeOptions: this.listingTypeOptions,
					typeOptions: this.typeOptions
				}),
				() => {
					this.emitParams();
				},
				{
					deep: true
				}
			);
		},
		beforeUnmount() {
			window.removeEventListener("click", this.closeModals);
		},
		methods: {
			formatLabel,
			parseHash(url) {
				const vm = this;

				var queryIndex = {
					q: "search",
					minyear: "minyear",
					water: "waterfront",
					minprice: "price",
					maxprice: "price",
					leaserate: "leaseRate",
					minacres: "acres",
					maxacres: "acres",
					minarea: "area",
					maxarea: "area",
					status: "status",
					broker: "broker",
					city: "city",
					county: "county",
					usage: "usage",
					minclosed: "closed",
					maxclosed: "closed"
				};

				var query = url.split("?")[1];
				var vars = query.split("&");

				for (var i in vars) {
					if (vars[i] === true) {
						continue;
					}
					var pair = vars[i].split("=");
					var key = decodeURIComponent(pair[0]);
					var value = decodeURIComponent(pair[1]);

					if (key in queryIndex) {
						var vueKey = queryIndex[key];
						if (key == "minprice" && value <= 1) {
							//console.log("minprice is deafult");
						} else if (key.includes("min")) {
							vm[vueKey]["min"] = value;
						} else if (key.includes("max")) {
							vm[vueKey]["max"] = value;
						} else {
							vm[vueKey] = value;
						//	console.log(vueKey + ":" + value);
						}
					} else if (key == "type") {
					//	console.log("found!");
						Object.keys(vm.typeOptions).forEach(
							v => (vm.typeOptions[v] = false)
						);
						value.split(",").forEach(type => {
							if (type in vm.typeOptions) {
								vm.typeOptions[type] = true;
							}
						});
					} else if (key == "listingtype") {
						Object.keys(vm.listingTypeOptions).forEach(
							v => (vm.listingTypeOptions[v] = false)
						);
						if (value in vm.listingTypeOptions) {
							vm.listingTypeOptions[value] = true;
						}
					}
				}
			},
			closeModals() {
				this.selectedFilter = null;
			},
			toggleFilter(filter) {
				if (this.selectedFilter != filter) {
					this.selectedFilter = filter;
				} else {
					this.selectedFilter = null;
				}
			},
			resetFilters() {
				this.search = "";
				this.minyear = "";
				this.community = "";
				this.waterfront = "";
				this.selectedFilter = null;
				this.features = "";
				this.status = "";
				this.broker = "";
				this.city = "";
				this.county = "";
				this.usage = "";
				this.price = {
					min: "",
					max: ""
				};
				this.leaseRate = "";
				this.acres = {
					min: "",
					max: ""
				};
				this.area = {
					min: "",
					max: ""
				};
				this.closed = {
					min: "",
					max: ""
				};
				this.lot = {
					min: "",
					max: ""
				};
				this.listingTypeOptions = {
					sale: true,
					lease: true
				};
				this.typeOptions = {
					land: true,
					office: true,
					retail: true,
					residential: true,
					industrial: true,
					specialty: true,
					healthcare: true,
					hospitality: true,
					entertainment: true
				};

				this.$nextTick(() => {
					this.emitParams();
				});
			},
			emitParams() {
				let searchParams = this.params.search;
				let queryParams = "";
				let filters = [];

				searchParams.query = this.search;

				if (this.search) {
					queryParams += "q=" + encodeURI(this.search) + "&";
				}

				if (this.typeActive) {
					let activeTypes = Object.keys(this.typeOptions).filter(i => {
						return this.typeOptions[i];
					});
					let combinedTypes = activeTypes.join(" OR types:");
					filters.push(`(types:${combinedTypes})`);

					queryParams +=
						"type=" +
						Object.keys(this.typeOptions)
							.filter(i => {
								return this.typeOptions[i];
							})
							.join(",") +
						"&";
				}

				if (this.listingTypeActive) {
					if (this.listingTypeOptions.sale) {
						filters.push(`listingType.sale:true`);
					} else if (this.listingTypeOptions.lease) {
						filters.push(`listingType.lease:true`);
					}

					queryParams +=
						"listingtype=" +
						(this.listingTypeOptions.sale ? "sale" : "lease") +
						"&";
				}

				if (this.price.min) {
					queryParams += "minprice=" + encodeURI(this.price.min) + "&";
				}
				if (this.price.max) {
					queryParams += "maxprice=" + encodeURI(this.price.max) + "&";
				}
				if (this.leaseRate) {
					queryParams += "leaserate=" + encodeURI(this.leaseRate) + "&";
				}

				if (this.price.min || this.price.max) {
					filters.push("settings.inPackageOnly:false");
				}

				if (this.listingTypeOptions.sale) {
					if (this.price.min && this.price.max) {
						filters.push(
							`pricing.sale.price >= ${this.price.min} AND pricing.sale.price <= ${this.price.max}`
						);
					} else if (this.price.min) {
						filters.push(`pricing.sale.price >= ${this.price.min}`);
					} else if (this.price.max) {
						filters.push(`pricing.sale.price <= ${this.price.max}`);
					}
				} else if (this.listingTypeOptions.lease) {
					if (this.leaseRate == "mo") {
						if (this.price.min && this.price.max) {
							filters.push(
								`pricing.lease.mo >= ${this.price.min} AND pricing.lease.mo <= ${this.price.max}`
							);
						} else if (this.price.min) {
							filters.push(`pricing.lease.mo >= ${this.price.min}`);
						} else if (this.price.max) {
							filters.push(`pricing.lease.mo <= ${this.price.max}`);
						}
					} else if (this.leaseRate == "sf") {
						if (this.price.min && this.price.max) {
							filters.push(
								`pricing.lease.sf.min <= ${this.price.max} AND pricing.lease.sf.max >= ${this.price.min}`
							);
						} else if (this.price.min) {
							filters.push(
								`pricing.lease.sf.max >= ${this.price.min}`
							);
						} else if (this.price.max) {
							filters.push(
								`pricing.lease.sf.min <= ${this.price.max}`
							);
						}
					}
				}

				if (this.area.min) {
					queryParams += "minarea=" + encodeURI(this.area.min) + "&";
				}
				if (this.area.max) {
					queryParams += "maxarea=" + encodeURI(this.area.max) + "&";
				}

				if (this.area.min && this.area.max) {
					filters.push(
						`sf.min <= ${this.area.max} AND sf.max >= ${this.area.min}`
					);
				} else if (this.area.min) {
					filters.push(`sf.max >= ${this.area.min}`);
				} else if (this.area.max) {
					filters.push(`sf.min > 0 AND sf.min <= ${this.area.max}`);
				}

				if (this.acres.min) {
					queryParams += "minacres=" + encodeURI(this.acres.min) + "&";
				}
				if (this.acres.max) {
					queryParams += "maxacres=" + encodeURI(this.acres.max) + "&";
				}

				if (this.acres.min && this.acres.max) {
					filters.push(`acres:${this.acres.min} TO ${this.acres.max}`);
				} else if (this.acres.min) {
					filters.push(`acres >= ${this.acres.min}`);
				} else if (this.acres.max) {
					filters.push(`acres > 0 AND acres <= ${this.acres.max}`);
				}

				if (
					this.area.min ||
					this.area.max ||
					this.acres.min ||
					this.acres.max
				) {
					filters.push(`NOT entryType:package`);
				}

				if (this.broker) {
					filters.push(`brokers:${this.broker}`);

					queryParams += "broker=" + encodeURI(this.broker) + "&";
				}

				if (this.city) {
					filters.push(`city:${this.city}`);

					queryParams += "city=" + encodeURI(this.city) + "&";
				}

				if (this.county) {
					filters.push(`county:${this.county}`);

					queryParams += "county=" + encodeURI(this.county) + "&";
				}

				if (this.usage) {
					filters.push(`usage:${this.usage}`);

					queryParams += "usage=" + encodeURI(this.usage) + "&";
				}

				if(this.status && !this.salesDatabase){
					filters.push(`status.value:${this.status}`);
					
					queryParams += "status=" + encodeURI(this.status) + "&";
				} else if (this.salesDatabase) {
					this.status = null;
					filters.push(`status.value:closed`);
				} else {
					filters.push(`status.value:pending OR status.value:active`);
				}

				if (this.salesDatabase) {
					if (this.closed.min) {
						queryParams +=
							"minclosed=" + encodeURI(this.closed.min) + "&";
					}
					if (this.closed.max) {
						queryParams +=
							"maxclosed=" + encodeURI(this.closed.max) + "&";
					}

					if (this.closed.min && this.closed.max) {
						filters.push(
							`dateClosed:${Math.round(
								new Date(this.closed.min).getTime() / 1000
							)} TO ${Math.round(
								new Date(this.closed.max).getTime() / 1000
							)}`
						);
					} else if (this.closed.min) {
						filters.push(
							`dateClosed >= ${Math.round(
								new Date(this.closed.min).getTime() / 1000
							)}`
						);
					} else if (this.closed.max) {
						filters.push(
							`dateClosed <= ${Math.round(
								new Date(this.closed.max).getTime() / 1000
							)}`
						);
					}
				}

				let combinedFilters = filters.join(" AND ");
				searchParams.options = {
					filters: combinedFilters
				};

				this.params.query = queryParams;

				this.$emit("updateSearchParams", this.params);
			}
		},
		created() {
			var vm = this;

			this.search = this.initialSearch;
			var initialType = this.initialType;
			var typeOptions = this.typeOptions;

			// If the type is set in the qs and it's a valid type
			if (initialType && initialType in typeOptions) {
				// Loop through types and set only the initial type to true
				for (var option in typeOptions) {
					if (initialType == option) {
						typeOptions[option] = true;
					} else {
						typeOptions[option] = false;
					}
				}
			}
			
			// Set brokers
			this.brokers = window.wwBrokers;
			// Set status
			this.statusOptions = window.wwPublicStatuses;
			// Set cities
			this.cities = window.wwCities;
			// Set counties
			this.counties = window.wwCounties;
			// Set property usages
			this.usages = window.wwUsages;
		},
		// updated() {
		// 	this.emitParams()
		// },
		components: {
			SearchInput,
			CalendarInput,
			vSelect,
			vCheck
		}
	};
</script>
